import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, ComponentPropsWithRef, KeyboardEvent, ReactNode } from 'react';
import { IconMinus, IconPlus } from '@tabler/icons-react';

import { cn } from '../../../lib/cn';
import { formatNumber } from '../../../i18n/format';
import { useTranslation } from '../../../i18n/LocaleProvider';
import { PRESS } from '../../../interaction';
import { Spinner } from '../../core/Spinner';
import { Field, fieldDescribedBy } from './Field';
import { useField } from './useField';
import { controlShellClass, SHELL_AFFIX, SHELL_CONTROL } from './controlStyles';
import type { ControlSize } from './controlStyles';

/* =============================================================================
   InputNumber
   -----------------------------------------------------------------------------
   A numeric field whose contract is `number | null` in, `number | null` out.

   Three decisions carry the whole component:

     1. The element is `type="text"` + `inputMode="decimal"`, never
        `type="number"`. The native number input refuses intermediate states
        (`-`, `1.`, an empty box) and draws its own spinner chrome, so it cannot
        host a draft string. The numeric semantics are declared instead with
        `role="spinbutton"` + `aria-value*`, which is what a screen reader reads.

     2. The component owns a `draft` string alongside the numeric prop. The
        draft is plain ASCII digits and one dot and is NEVER reformatted while
        the user types — reformatting moves the caret and eats typed decimals.
        Locale presentation belongs to `aria-valuetext` (via `formatNumber`),
        which is where a screen reader picks it up.

     3. The stepper lives INSIDE the box, which is the only reason this control
        uses `controlShellClass` rather than `controlBoxClass`: the shell draws
        the focus ring with `focus-within` and the borderless input opts out of
        the global one with `data-focus-ring="none"`.
   ============================================================================= */

/** The only shape the draft accepts: ASCII digits, at most one dot, one sign. */
const DRAFT_PATTERN = /^-?\d*\.?\d*$/;

/**
 * Parses the draft. `null` — never `Number.isNaN` — is this library's "no
 * value": an empty box, a bare `-`, a lone `.` and unparseable text all mean
 * "nothing typed yet".
 */
function parseDraft(draft: string): number | null {
  if (draft === '') return null;
  if (!DRAFT_PATTERN.test(draft)) return null;
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? parsed : null;
}

/** `value` → the draft string. `null` is the empty box. */
function toDraft(value: number | null, precision: number): string {
  if (value === null || !Number.isFinite(value)) return '';
  return value.toFixed(precision);
}

/** How many digits the draft carries after the dot. */
function decimalCount(draft: string): number {
  const dot = draft.indexOf('.');
  return dot === -1 ? 0 : draft.length - dot - 1;
}

/**
 * The field's own type validation. `useField` never calls it for an empty
 * value, so every branch here is about text the user actually typed.
 */
function validateDraft(
  draft: string,
  { min, max, precision }: { min?: number; max?: number; precision: number },
): string | null {
  const parsed = parseDraft(draft);
  if (parsed === null) return 'Enter a number';
  if (min !== undefined && parsed < min) return `Enter a value of at least ${min}`;
  if (max !== undefined && parsed > max) return `Enter a value of at most ${max}`;
  if (decimalCount(draft) > precision) {
    return precision === 1 ? 'Use at most 1 decimal place' : `Use at most ${precision} decimal places`;
  }
  return null;
}

/**
 * The stepper's skin: small, quiet, and full-height so the target is 24×40
 * rather than 24×24.
 *
 * Deliberate: no `dhd-target`. Both buttons live inside the shell, which
 * carries its own stacking position above `Field`'s hit-target overlay, so the
 * buttons need none of their own — and two 24px controls 32px apart cannot hold
 * non-overlapping 44px overlays. They would overlap by 12px, and because the
 * later button paints last, its overlay would sit over the right edge of
 * Decrease: pressing "−" there would *increase* the value. A wrong action is a
 * worse failure than a small target, so the overlay is not used here.
 */
const STEPPER_CLASS = cn(
  SHELL_AFFIX,
  'relative grid h-full w-6 place-items-center rounded-xs',
  'hover:text-heading',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
  'disabled:cursor-not-allowed disabled:opacity-45',
);

export interface InputNumberProps extends Omit<ComponentPropsWithRef<'div'>, 'onChange' | 'children'> {
  id: string;
  label?: ReactNode;
  description?: ReactNode;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** How far one step (a click or an arrow key) moves the value. */
  step?: number;
  /** Decimal places, for display and for the "too many decimals" rule. */
  precision?: number;
  required?: boolean;
  /** Submit-level or server override. Wins over the field's own validation. */
  error?: string | null;
  success?: boolean;
  successMessage?: ReactNode;
  size?: ControlSize;
  disabled?: boolean;
  readOnly?: boolean;
  /** Raised while the value is being saved: the stepper becomes a spinner. */
  loading?: boolean;
  /** Bumped by the form on each submit attempt; reveals the error. */
  submitCount?: number;
  placeholder?: string;
}

export function InputNumber({
  id,
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 1,
  precision = 0,
  required = false,
  error,
  success = false,
  successMessage,
  size = 'md',
  disabled = false,
  readOnly = false,
  loading = false,
  submitCount = 0,
  placeholder,
  ...rest
}: InputNumberProps) {
  const { t } = useTranslation();
  // The draft is the input's real value: the user must be able to sit in `-`,
  // `1.` or an empty box, and none of those is a number yet.
  const [draft, setDraft] = useState(() => toDraft(value, precision));
  const [lastValue, setLastValue] = useState(value);

  // The input is kept here so focus can be held across the stepper swap below.
  const inputRef = useRef<HTMLInputElement>(null);

  // Adjusting state during render, not in an effect — React re-runs this render
  // before committing, so the box never flashes a stale number.
  // `Object.is`, not `!==`: a non-finite `value` would otherwise read as changed
  // on every render, and a render-phase update that never settles makes React
  // throw "Too many re-renders" and blanks the tree.
  if (!Object.is(lastValue, value)) {
    setLastValue(value);
    // Re-seed only when the prop disagrees with what is typed. A parent that
    // echoes `onChange` would otherwise rewrite `1.` to `1` — and wipe a bare
    // `-` — mid-keystroke, which is exactly the decimal-and-caret damage the
    // draft exists to prevent.
    if (parseDraft(draft) !== value) setDraft(toDraft(value, precision));
  }

  const field = useField({
    value: draft,
    onChange: (next) => onChange(parseDraft(next)),
    validate: (next) => validateDraft(next, { min, max, precision }),
    required,
    submitCount,
    success,
  });

  // The steppers unmount while `loading` swaps in the spinner, which would drop
  // focus to <body> mid-interaction. The input is the only stable target left.
  useEffect(() => {
    if (loading) inputRef.current?.focus();
  }, [loading]);

  const isDisabled = disabled || loading;
  const message = error ?? field.error;
  const invalid = Boolean(message);
  const savedMessage = field.success ? (successMessage ?? t('ds.number.saved')) : undefined;
  const describedBy = fieldDescribedBy(id, {
    description: Boolean(description),
    message: Boolean(message ?? savedMessage),
  });

  const numeric = parseDraft(draft);
  // Stepping from an empty or invalid box starts where the range starts.
  const base = numeric ?? min ?? 0;
  const canStepDown = !isDisabled && !readOnly && (min === undefined || base > min);
  const canStepUp = !isDisabled && !readOnly && (max === undefined || base < max);
  // ARIA requires `aria-valuenow` to sit inside the declared range, so a typed
  // 150 against `max={100}` reports no value rather than an illegal one.
  const outOfRange =
    numeric !== null && ((min !== undefined && numeric < min) || (max !== undefined && numeric > max));

  function spin(direction: 1 | -1) {
    // A read-only field still takes focus and arrow keys, so the guard is here
    // rather than only on the buttons' `disabled`.
    if (isDisabled || readOnly) return;
    const from = parseDraft(draft) ?? min ?? 0;
    let next = from + direction * step;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    const text = toDraft(Number(next.toFixed(precision)), precision);
    setDraft(text);
    field.setValue(text);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    setDraft(next);
    field.setValue(next);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    // The modifiers belong to the browser: Shift extends a selection, and
    // Cmd/Ctrl/Alt are caret and word operations.
    if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;
    if (isDisabled || readOnly) return;
    const direction = event.key === 'ArrowUp' ? 1 : -1;
    // The same gate the buttons carry, so a disabled stepper cannot be reached
    // through the keyboard.
    if (direction === 1 ? !canStepUp : !canStepDown) return;
    // The caret must not move: the vertical arrows belong to the number, not to
    // the text.
    event.preventDefault();
    spin(direction);
  }

  return (
    <Field
      {...rest}
      id={id}
      label={label}
      description={description}
      required={required}
      disabled={isDisabled}
      invalid={invalid}
      error={message}
      successMessage={savedMessage}
      size={size}
    >
      <div
        className={controlShellClass({
          size,
          invalid,
          success: Boolean(savedMessage),
          disabled: isDisabled,
          readOnly,
        })}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="decimal"
          role="spinbutton"
          autoComplete="off"
          data-focus-ring="none"
          className={SHELL_CONTROL}
          value={draft}
          placeholder={placeholder}
          disabled={isDisabled}
          readOnly={readOnly}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          // The consumer's own `onBlur` sits on the root element, where React's
          // focus events bubble to it — so it is not composed here, or it would
          // run twice.
          onBlur={field.onBlur}
          // The form is `noValidate` and this library's error UI owns validation,
          // so the native `required` attribute stays off and `aria-required` speaks.
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          aria-busy={loading || undefined}
          aria-describedby={describedBy}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={numeric !== null && !outOfRange ? numeric : undefined}
          aria-valuetext={
            numeric === null ? undefined : formatNumber(numeric, { precision, grouping: false })
          }
        />

        {loading ? (
          <Spinner size="sm" aria-hidden="true" />
        ) : (
          <>
            <button
              type="button"
              aria-label={t('ds.number.decrease')}
              disabled={!canStepDown}
              onClick={() => spin(-1)}
              className={STEPPER_CLASS}
            >
              <IconMinus className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={t('ds.number.increase')}
              disabled={!canStepUp}
              onClick={() => spin(1)}
              className={STEPPER_CLASS}
            >
              <IconPlus className="size-4" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </Field>
  );
}
